import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Plus, XCircle } from "lucide-react";
import { billingApi, type Entitlement } from "./api";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { usePermission } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

const STATUS_FILTERS = [
  { value: "active", label: "Activos" },
  { value: "grace", label: "En gracia" },
  { value: "suspended", label: "Suspendidos" },
  { value: "cancelled", label: "Cancelados" },
];

// ESPEJO de mn-server/src/billing/features/catalog.ts (FEATURE_KEYS). Es el
// dropdown del grant manual de Nexus → debe listar las 23 keys para que un
// admin pueda otorgar a mano cualquier feature monetizable (incluidas las
// F4/F5: placements, spotlights, editorial, push). Mantener sincronizado.
const FEATURE_OPTIONS = [
  "cloakroom", "lost_and_found", "support_live", "roles_advanced", "analytics_advanced",
  "profile_pro", "catalog_pro", "booking_pro", "verification", "provider_featured",
  "producer_calendar", "sales_inventory",
  "event_featured", "trending_boost",
  "artist_spotlight", "provider_spotlight", "provider_banner", "push_blast",
  "news_publication", "newsletter_inclusion", "dj_set_drop", "cultural_boost",
  "landing_premium",
];

export function EntitlementsScreen() {
  const qc = useQueryClient();
  const { push } = useToast();
  const canManage = usePermission(PERMISSIONS.BILLING_ENTITLEMENTS_MANAGE);
  const [status, setStatus] = useState("active");
  const [showGrant, setShowGrant] = useState(false);

  const q = useQuery({
    queryKey: ["billing", "entitlements", status],
    queryFn: () => billingApi.listEntitlements({ status }),
  });

  const revoke = useMutation({
    mutationFn: (id: number) => billingApi.revoke(id),
    onSuccess: () => {
      push({ kind: "success", message: "Entitlement revocado." });
      qc.invalidateQueries({ queryKey: ["billing", "entitlements"] });
    },
    onError: () => push({ kind: "error", message: "No se pudo revocar." }),
  });

  const rows = q.data ?? [];

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Facturación · Suscripciones</p>
          <h1 className="nx-h1">Entitlements activos</h1>
          <p className="text-sm text-fg-muted mt-1">
            Quién tiene qué feature desbloqueada. En beta podés otorgar manualmente (cobro one-shot /
            transferencia fuera de plataforma) o revocar. La recurrencia automática llega post-MVP.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => q.refetch()} className="nx-btn-secondary" disabled={q.isFetching}>
            <RefreshCw size={14} className={q.isFetching ? "animate-spin" : ""} /> Actualizar
          </button>
          {canManage && (
            <button onClick={() => setShowGrant(true)} className="nx-btn-primary">
              <Plus size={14} /> Otorgar manual
            </button>
          )}
        </div>
      </header>

      <div className="flex items-center gap-1.5">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatus(f.value)}
            className={"px-3 py-1.5 rounded-md text-xs font-medium transition-colors " + (status === f.value ? "bg-accent text-fg-inverted" : "bg-bg-subtle text-fg-muted hover:text-fg")}
          >
            {f.label}
          </button>
        ))}
      </div>

      {q.isLoading ? (
        <div className="text-sm text-fg-subtle py-10 text-center">Cargando…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border-subtle rounded-lg py-12 text-center text-sm text-fg-muted">Sin entitlements {STATUS_FILTERS.find((f) => f.value === status)?.label.toLowerCase()}.</div>
      ) : (
        <div className="overflow-x-auto border border-border-subtle rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-subtle bg-bg-subtle text-2xs uppercase tracking-wide text-fg-subtle">
                <th className="text-left font-semibold px-3 py-2">Sujeto</th>
                <th className="text-left font-semibold px-3 py-2">Feature</th>
                <th className="text-left font-semibold px-3 py-2">Origen</th>
                <th className="text-left font-semibold px-3 py-2">Vence</th>
                <th className="text-right font-semibold px-3 py-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e: Entitlement) => (
                <tr key={e.id} className="border-b border-border-subtle last:border-0 hover:bg-bg-hover">
                  <td className="px-3 py-2 text-xs">{e.subjectType} #{e.subjectId}{e.scopeEventId ? ` · ev ${e.scopeEventId}` : ""}</td>
                  <td className="px-3 py-2 font-medium text-fg">{e.featureKey}</td>
                  <td className="px-3 py-2 text-xs text-fg-muted">{e.source}{e.skuKey ? ` · ${e.skuKey}` : ""}</td>
                  <td className="px-3 py-2 text-xs text-fg-subtle">{e.expiresAt ? new Date(e.expiresAt).toLocaleDateString("es-CL") : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {canManage && e.status !== "cancelled" && (
                      <button
                        className="nx-btn-danger"
                        disabled={revoke.isPending}
                        onClick={async () => {
                          const ok = await confirmAction({ title: "Revocar entitlement", message: `${e.featureKey} de ${e.subjectType} #${e.subjectId}`, confirmLabel: "Revocar", tone: "danger" });
                          if (ok === true) revoke.mutate(e.id);
                        }}
                      >
                        <XCircle size={13} /> Revocar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showGrant && canManage && <GrantModal onClose={() => setShowGrant(false)} onDone={() => { setShowGrant(false); qc.invalidateQueries({ queryKey: ["billing", "entitlements"] }); }} />}
    </div>
  );
}

function GrantModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { push } = useToast();
  const [subjectType, setSubjectType] = useState("CLUB");
  const [subjectId, setSubjectId] = useState("");
  const [featureKey, setFeatureKey] = useState(FEATURE_OPTIONS[0]);
  const [durationDays, setDurationDays] = useState("30");
  const [note, setNote] = useState("");

  const grant = useMutation({
    mutationFn: () => billingApi.grant({ subjectType, subjectId: Number(subjectId), featureKey, durationDays: Number(durationDays) || undefined, note: note || undefined }),
    onSuccess: () => { push({ kind: "success", message: "Entitlement otorgado." }); onDone(); },
    onError: () => push({ kind: "error", message: "No se pudo otorgar." }),
  });

  const canSave = subjectId && Number.isFinite(Number(subjectId));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 py-10">
      <div className="w-full max-w-lg rounded-lg bg-bg-base border border-border-subtle shadow-xl p-5 flex flex-col gap-3">
        <h2 className="text-base font-semibold text-fg">Otorgar entitlement manual</h2>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1"><span className="text-xs text-fg-muted">Tipo de sujeto</span>
            <select className="nx-input" value={subjectType} onChange={(e) => setSubjectType(e.target.value)}>
              <option value="CLUB">CLUB</option><option value="ARTIST">ARTIST</option><option value="PARTNER">PARTNER</option>
            </select>
          </label>
          <label className="flex flex-col gap-1"><span className="text-xs text-fg-muted">ID del sujeto</span>
            <input className="nx-input" value={subjectId} onChange={(e) => setSubjectId(e.target.value)} placeholder="123" />
          </label>
          <label className="flex flex-col gap-1"><span className="text-xs text-fg-muted">Feature</span>
            <select className="nx-input" value={featureKey} onChange={(e) => setFeatureKey(e.target.value)}>
              {FEATURE_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1"><span className="text-xs text-fg-muted">Duración (días, vacío = perpetuo)</span>
            <input className="nx-input" value={durationDays} onChange={(e) => setDurationDays(e.target.value)} placeholder="30" />
          </label>
        </div>
        <label className="flex flex-col gap-1"><span className="text-xs text-fg-muted">Nota</span>
          <input className="nx-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Cortesía / pago por transferencia / beta" />
        </label>
        <div className="flex items-center justify-end gap-2 mt-1">
          <button onClick={onClose} className="nx-btn-secondary">Cancelar</button>
          <button onClick={() => grant.mutate()} disabled={!canSave || grant.isPending} className="nx-btn-primary">
            {grant.isPending ? "Otorgando…" : "Otorgar"}
          </button>
        </div>
      </div>
    </div>
  );
}
